/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from '../../app/store';
import pearlApi from '../sharedcomponents/api/pearlApi';
import { snackNotifications } from '../sharedcomponents/notification/SnackBarUtils';
import { bookmarkCurrentDocumentThunk } from '../sharedcomponents/chat/chatSlice';

export interface List {
  doc_id: string;
  doc_version_id: string;
  title: string;
  created_at: string;
  icons?: string;
  actions?: string;
}

export interface FavoriteState {
  favorites: List[];
  fetchingFavoriteList: boolean;
}

const initialState: FavoriteState = {
  favorites: [],
  fetchingFavoriteList: false,
};

export const fetchFavoriteListThunk = createAsyncThunk<List[]>('fetchFavoriteListThunk', async () => {
  try {
    const {
      data: { data },
    } = await pearlApi.get(`/bookmarks/document`);
    return data;
  } catch (err) {
    snackNotifications.error(`Failed to fetch favorite list: ${(err as Error).message}`);
    throw err;
  }
});

export const removeFavoriteThunk = createAsyncThunk<string, { documentId: string }, { state: RootState }>(
  'removeFavoriteThunk',
  async ({ documentId }, { dispatch }) => {
    try {
      await dispatch(bookmarkCurrentDocumentThunk({ documentId, bookmark: false })).unwrap();
      return documentId;
    } catch (err) {
      snackNotifications.error(`Failed to remove favorite: ${(err as Error).message}`);
      throw err;
    }
  }
);

export const favoriteSlice = createSlice({
  name: 'favorite',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    setFavoriteList: (state, action: PayloadAction<List[]>) => {
      state.favorites = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFavoriteListThunk.pending, (state) => {
        state.fetchingFavoriteList = true;
      })
      .addCase(fetchFavoriteListThunk.fulfilled, (state, data) => {
        state.favorites = data.payload;
        state.fetchingFavoriteList = false;
      })
      .addCase(fetchFavoriteListThunk.rejected, (state) => {
        state.fetchingFavoriteList = false;
      })
      .addCase(removeFavoriteThunk.fulfilled, (state, data) => {
        state.favorites = state.favorites.filter(({ doc_id }) => data.payload !== doc_id);
      });
  },
});

export const { setFavoriteList } = favoriteSlice.actions;

export const selectFavoriteState = (state: RootState): FavoriteState => state.favorite;

export default favoriteSlice.reducer;
